/**
 * @module EvidenceContainersService
 * @purpose API service for the evidence-container-foundation PRP's
 *   registration surface (transport probe, staged registration) and the
 *   container-inventory discovery trigger/read.
 * @depends apiClient from ./api; evidence-containers types
 * @dependedOnBy Later hooks/components layers (Layers 7-8 of this feature).
 *
 * @rules
 * - Performance: inventory reads are unpaginated by design (backend caps at
 *   ~1,000 items per receipt) -- no client-side pagination params here.
 * - State: stateless service layer; all state managed in hooks.
 * - Errors: `ApiError` propagated to callers -- no empty catch blocks.
 *
 * URLs mirror `backend/crates/evidenx-api/src/routes.rs` exactly. Note the
 * container-inventory routes are scoped by `case_id` + `receipt_id` only (no
 * `request_id` in the path) -- `evidence_containers::handle_trigger_inventory`
 * / `handle_get_inventory` take `Path<(Uuid, Uuid)>` for `(case_id,
 * receipt_id)`.
 */

import { apiClient } from './api'
import type {
  GroupedInventoryResponse,
  InventoryQueryParams,
  InventoryTriggerResponse,
  ProbeReceiptRequest,
  ProbeReceiptResponse,
  RegisterStagedRequest,
  RegisterStagedResponse,
  ReverifyReceiptResponse,
} from '../types/evidence-containers'

/**
 * Decide transport before any bytes move.
 *
 * `POST /api/cases/:id/collection-requests/:requestId/receipts/probe`
 */
export async function probeReceiptTransport(
  caseId: string,
  requestId: string,
  body: ProbeReceiptRequest
): Promise<ProbeReceiptResponse> {
  return apiClient.post<ProbeReceiptResponse>(
    `/cases/${caseId}/collection-requests/${requestId}/receipts/probe`,
    body
  )
}

/**
 * Register a staged container by reference and start background
 * verification.
 *
 * `POST /api/cases/:id/collection-requests/:requestId/receipts/register-staged`
 */
export async function registerStagedReceipt(
  caseId: string,
  requestId: string,
  body: RegisterStagedRequest
): Promise<RegisterStagedResponse> {
  return apiClient.post<RegisterStagedResponse>(
    `/cases/${caseId}/collection-requests/${requestId}/receipts/register-staged`,
    body
  )
}

/**
 * Re-trigger verification for a `transport='staged'` receipt whose previous
 * pass failed (or was interrupted mid-copy) -- the retry path spec's edge
 * case: "receipt remains unverified and re-triggerable".
 *
 * `POST /api/cases/:id/collection-receipts/:receiptId/reverify`
 */
export async function reverifyStagedReceipt(
  caseId: string,
  receiptId: string
): Promise<ReverifyReceiptResponse> {
  return apiClient.post<ReverifyReceiptResponse>(
    `/cases/${caseId}/collection-receipts/${receiptId}/reverify`
  )
}

/**
 * Trigger a container-inventory discovery pass for a verified receipt.
 *
 * `POST /api/cases/:id/collection-receipts/:receiptId/inventory`
 */
export async function triggerContainerInventory(
  caseId: string,
  receiptId: string
): Promise<InventoryTriggerResponse> {
  return apiClient.post<InventoryTriggerResponse>(
    `/cases/${caseId}/collection-receipts/${receiptId}/inventory`
  )
}

function buildInventoryQuery(params: InventoryQueryParams | undefined): string {
  if (!params) return ''
  const searchParams = new URLSearchParams()
  if (params.groupBy) searchParams.set('groupBy', params.groupBy)
  if (params.kind) searchParams.set('kind', params.kind)
  const query = searchParams.toString()
  return query ? `?${query}` : ''
}

/**
 * Read a receipt's inventory, grouped by artefact type and optionally
 * filtered by item kind.
 *
 * `GET /api/cases/:id/collection-receipts/:receiptId/inventory?groupBy=artefactType&kind=…`
 */
export async function getContainerInventory(
  caseId: string,
  receiptId: string,
  params?: InventoryQueryParams
): Promise<GroupedInventoryResponse> {
  const query = buildInventoryQuery(params)
  return apiClient.get<GroupedInventoryResponse>(
    `/cases/${caseId}/collection-receipts/${receiptId}/inventory${query}`
  )
}
